import { fetchSiteComprehensiveStatus } from '@/remote/dashboard/comprehensive-status';
import { QUERY_KEYS } from '@/remote/query-key';
import type { SiteComprehensiveStatus } from '@/types/site';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

interface UseSiteComprehensiveStatusOptions {
  enabled?: boolean;
  staleTime?: number;
  gcTime?: number;
  refetchInterval?: number;
}

interface UseSiteComprehensiveStatusReturn {
  data: SiteComprehensiveStatus | undefined;
  isLoading: boolean;
  error: Error | null;
  isError: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  actions: {
    refetch: () => void;
    invalidate: () => void;
  };
}

export const useSiteComprehensiveStatus = (
  siteId: string,
  options: UseSiteComprehensiveStatusOptions = {}
): UseSiteComprehensiveStatusReturn => {
  const queryClient = useQueryClient();

  const {
    enabled = true,
    staleTime = 1000 * 60, // 1분 - 실시간 데이터이므로 짧게 설정
    gcTime = 1000 * 60 * 5, // 5분
    refetchInterval = 1000 * 60, // 1분마다 자동 갱신
  } = options;

  const query = useQuery<SiteComprehensiveStatus>({
    queryKey: QUERY_KEYS.dashboard.comprehensiveStatus(siteId),
    queryFn: () => fetchSiteComprehensiveStatus(siteId),
    enabled: enabled && !!siteId,
    staleTime,
    gcTime,
    refetchInterval,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  const { refetch } = query;

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.dashboard.comprehensiveStatus(siteId),
    });
  }, [queryClient, siteId]);

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    isError: query.isError,
    isFetching: query.isFetching,
    isSuccess: query.isSuccess,
    actions: {
      refetch,
      invalidate,
    },
  };
};
